import { ListItem, ListItemButton, Checkbox, Stack, Typography, Button, Box, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip } from '@mui/material';

import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

export default function ShiftUserList({
    users,
    selectedUsers,
    setSelectedUsers,
    overAllocateUserMap,
    addOverAllocateMember,
    removeOverAllocateMember
}) {

    const {
        Shifts,
        Campaigns,
        Languages
    } = useSelector((state) => state.user || {});

    // State for selected languages filter
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    // State for filtered users
    const [filteredUsers, setFilteredUsers] = useState(users);

    // Filter users when selected languages change
    useEffect(() => {
        if (selectedLanguages.length === 0) {
            // If no languages selected, show all users
            setFilteredUsers(users);
        } else {
            // Filter users based on selected languages
            const filtered = users.filter(user => {
                // Check if user has any of the selected languages
                return user.languages && Array.isArray(user.languages) && user.languages.some(langId => {
                    // Handle both string IDs and object IDs
                    const langIdStr = typeof langId === 'object' ? langId._id : langId;
                    return selectedLanguages.includes(langIdStr);
                });
            });
            setFilteredUsers(filtered);
        }
    }, [selectedLanguages, users]);

    // Handle language selection change
    const handleLanguageChange = (event) => {
        const value = event.target.value;
        setSelectedLanguages(value);
    };

    // Handle removing a single language from selection
    const handleDeleteLanguage = (languageToDelete) => {
        const newSelectedLanguages = selectedLanguages.filter(
            (language) => language !== languageToDelete
        );
        setSelectedLanguages(newSelectedLanguages);
    };

    return (
        <>
            {/* Filter Bar */}
            <Box sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl fullWidth size="small">
                        <InputLabel id="language-filter-label">Filter by Languages</InputLabel>
                        <Select
                            labelId="language-filter-label"
                            multiple
                            onMouseDown={(event) => {
                                event.stopPropagation();
                            }}
                            value={selectedLanguages}
                            onChange={handleLanguageChange}
                            input={<OutlinedInput label="Filter by Languages" />}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 250
                                    }
                                }
                            }}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const language = Languages.find(lang => lang._id === value);
                                        return (
                                            <Chip
                                                key={value}
                                                label={language ? language.name : value}
                                                onDelete={() => handleDeleteLanguage(value)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {Languages && Languages.map((language) => (
                                <MenuItem key={language._id} value={language._id}>
                                    {language.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedLanguages.length > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedLanguages([])}
                            sx={{ minWidth: '100px' }}
                        >
                            Clear Filter
                        </Button>
                    )}
                </Stack>
            </Box>

            {filteredUsers.map((user, index) => (
                <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton sx={{ borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                        <Checkbox
                            disabled={
                                // if user attendance is not present
                                // !user?.attendance ||
                                // if user attendance status is not present
                                // user.attendance?.status !== 'present' ||
                                // if user is over allocated
                                (overAllocateUserMap.has(user._id) ? false : user.allocated >= user.limit) ||
                                // if user has approved leave request
                                user?.leaveRequest?.approved === 'Approved'
                            }
                            checked={selectedUsers.includes(user._id)}
                            onChange={(e) => {
                                const newSelected = [...selectedUsers];
                                if (e.target.checked) {
                                    newSelected.push(user._id);
                                } else {
                                    const index = newSelected.indexOf(user._id);
                                    if (index > -1) {
                                        newSelected.splice(index, 1);
                                    }
                                }
                                setSelectedUsers(newSelected);
                            }}
                        />
                        {/* Main row stack for content after checkbox */}
                        <Stack direction="row" sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'space-between', ml: 1 }}>
                            {/* Center column for Name and Languages */}
                            <Stack sx={{ flexGrow: 1, alignItems: 'center', textAlign: 'center', mr: 1, width: '100%', justifyContent: 'center' }} rowGap={1}>
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                    <Typography variant="body1" fontWeight="medium" align="center" textAlign="center">
                                        {user.name}
                                    </Typography>
                                    {/* {user.attendance?.status && (
                                        <Chip
                                            label={user.attendance.status}
                                            size="small"
                                            variant="outlined"
                                            color={
                                                user.attendance.status === 'present' ? 'success' :
                                                    user.attendance.status === 'absent' ? 'error' :
                                                        'default'
                                            }
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )} */}
                                </Stack>
                                {user.languages && user.languages.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, justifyContent: 'center' }}>
                                        {user.languages.map(langId => {
                                            const language = Languages.find(lang =>
                                                lang._id === (typeof langId === 'object' ? langId._id : langId)
                                            );
                                            return language ? (
                                                <Chip
                                                    key={language._id}
                                                    label={language.name}
                                                    size="small"
                                                    variant="outlined"
                                                    color="default"
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                                {user?.leaveRequest && (
                                    <Chip
                                        label={`On Leave: ${new Date(user.leaveRequest.from).toLocaleDateString()} - ${new Date(user.leaveRequest.to).toLocaleDateString()}, ${dayjs(user.leaveRequest.to).diff(dayjs(user.leaveRequest.from), 'day') + 1} days`}
                                        size="small"
                                        variant="outlined"
                                        color={user.leaveRequest.approved === 'Approved' ? 'error' : 'warning'}
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                )}
                            </Stack>
                            {/* Right column for Limit/Allocated and Button */}
                            <Stack
                                direction="column"
                                alignItems="flex-end"
                                spacing={0.5}
                                sx={{ minWidth: '120px' }} // Ensure minimum width for alignment
                            >
                                <Stack
                                    direction="row"
                                    spacing={1} // Adjusted spacing
                                    justifyContent={'flex-end'}
                                    sx={{
                                        color: user.allocated >= user.limit ? 'error.main' : 'success.main', // Use theme colors
                                        width: '100%'
                                    }}
                                >
                                    <Typography variant="caption" fontWeight="bold" textAlign="right">
                                        Limit: {user.limit || 0}
                                    </Typography>
                                    <Typography variant="caption" fontWeight="bold" textAlign="right">
                                        Allocated: {user.allocated || 0}
                                    </Typography>
                                </Stack>
                                {user.allocated >= user.limit && (
                                    <Button
                                        variant={overAllocateUserMap.has(user._id) ? "outlined" : "outlined"}
                                        size="small"
                                        color={overAllocateUserMap.has(user._id) ? "error" : "primary"}
                                        disabled={
                                            // if the map contains any user then need to disable the buttons for which user is not there
                                            overAllocateUserMap.size > 0 ? !overAllocateUserMap.has(user._id) : false
                                        }
                                        onClick={() => {
                                            overAllocateUserMap.has(user._id) ? removeOverAllocateMember(user) : addOverAllocateMember(user);
                                        }}>
                                        {overAllocateUserMap.has(user._id) ? "Remove" : "Overallocate"}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </ListItemButton>
                </ListItem>
            ))}

            {filteredUsers.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1, mt: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                        No users match the selected language filter.
                    </Typography>
                </Box>
            )}
        </>
    );
}
